import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/contexts/auth-context";

/**
 * CRM Events Bus client (task #103).
 *
 * Connects to `/ws/crm-events`, sends `{ type: 'auth', token }`, then
 * listens for server-pushed `{ type: 'event', resource, op, id }` messages
 * and translates them into `queryClient.invalidateQueries` calls so that
 * the affected lists refresh sub-second.
 *
 * Design notes:
 * - Provider only opens a socket when a CRM token is present.
 * - Auto-reconnect with capped exponential backoff (1s → 30s).
 * - Exposes `connected` so list pages can disable the polling fallback
 *   while the bus is healthy. If the bus is offline, polling kicks back in.
 *   See docs/audits/cache-strategy.md §9.
 */

const RESOURCE_TO_QUERY_KEYS: Record<string, string[]> = {
  customers: [
    "/api/crm/customers",
    "/api/crm/geo-analytics",
  ],
  "purchase-deeds": [
    "/api/crm/purchase-deeds",
    "/api/crm/purchase-deeds/orphan-count",
  ],
  "price-overrides": [
    "/api/crm/cms/price-overrides",
    "/api/crm/cms/active-price-overrides",
    "/api/quotations",
  ],
};

interface CrmEvent {
  type: "event";
  resource: string;
  op: "created" | "updated" | "deleted";
  id?: string;
  ts: number;
}

interface CrmEventsBusContextValue {
  connected: boolean;
}

const CrmEventsBusContext = createContext<CrmEventsBusContextValue>({
  connected: false,
});

export const useCrmEventsBus = () => useContext(CrmEventsBusContext);

interface CrmEventsBusProviderProps {
  children: ReactNode;
}

export function CrmEventsBusProvider({ children }: CrmEventsBusProviderProps) {
  const { token } = useAuth();
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const closedByUsRef = useRef(false);

  const handleEvent = useCallback((event: CrmEvent) => {
    const keys = RESOURCE_TO_QUERY_KEYS[event.resource];
    if (!keys) return;

    for (const key of keys) {
      // Use the array-prefix form so paginated/filtered keys
      // ([key, { page, search, ... }]) all match.
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, []);

  const connect = useCallback((authToken: string) => {
    if (typeof window === "undefined") return;
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) return;

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/ws/crm-events`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.error("[CrmEventsBus] Failed to open socket:", err);
      scheduleReconnect(authToken);
      return;
    }

    wsRef.current = ws;

    ws.onopen = () => {
      try {
        ws.send(JSON.stringify({ type: "auth", token: authToken }));
      } catch (err) {
        console.error("[CrmEventsBus] Auth send failed:", err);
      }
    };

    ws.onmessage = (msg) => {
      let data: any;
      try {
        data = JSON.parse(msg.data);
      } catch {
        return;
      }

      if (data?.type === "authenticated") {
        reconnectAttemptsRef.current = 0;
        setConnected(true);
        return;
      }

      if (data?.type === "event" && typeof data.resource === "string") {
        handleEvent(data as CrmEvent);
        return;
      }

      if (data?.type === "error") {
        console.warn("[CrmEventsBus] Server error:", data.message);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      if (closedByUsRef.current) {
        closedByUsRef.current = false;
        return;
      }
      scheduleReconnect(authToken);
    };

    ws.onerror = () => {
      // onclose runs right after; reconnect logic lives there.
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleEvent]);

  const scheduleReconnect = useCallback(
    (authToken: string) => {
      if (reconnectTimerRef.current) return;
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      // Capped exponential backoff: 1s, 2s, 4s, 8s, 16s, then 30s steady.
      const delay = Math.min(30_000, 1000 * Math.pow(2, Math.min(attempt - 1, 5)));
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect(authToken);
      }, delay);
    },
    [connect],
  );

  useEffect(() => {
    if (!token) {
      // Tear down any active connection if the user logged out.
      closedByUsRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* noop */
        }
        wsRef.current = null;
      }
      setConnected(false);
      return;
    }

    closedByUsRef.current = false;
    connect(token);

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* noop */
        }
        wsRef.current = null;
      }
      setConnected(false);
    };
  }, [token, connect]);

  // Reconnect promptly when the tab becomes visible again or the network
  // comes back online: the browser may have suspended the socket silently.
  useEffect(() => {
    if (!token) return;

    const tryReconnect = () => {
      const ws = wsRef.current;
      const isHealthy = ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);
      if (!isHealthy) {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        reconnectAttemptsRef.current = 0;
        connect(token);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") tryReconnect();
    };
    window.addEventListener("online", tryReconnect);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("online", tryReconnect);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [token, connect]);

  const value = useMemo<CrmEventsBusContextValue>(
    () => ({ connected }),
    [connected],
  );

  return (
    <CrmEventsBusContext.Provider value={value}>
      {children}
    </CrmEventsBusContext.Provider>
  );
}
